export type HubDataTableColumnLayoutSnapshot = {
  columnOrder: string[];
  hiddenColumnIds: string[];
};

const SNAPSHOT_VERSION = 1;

export function hubDataTableColumnLayoutStateKey(tableId: string): string {
  return `data-table:${tableId.trim()}`;
}

export function parseHubDataTableColumnLayoutSnapshot(raw: unknown): HubDataTableColumnLayoutSnapshot | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (record.v !== SNAPSHOT_VERSION) {
    return null;
  }
  const columnOrder = Array.isArray(record.columnOrder)
    ? record.columnOrder.filter((entry): entry is string => typeof entry === "string" && entry.trim() !== "")
    : [];
  const hiddenColumnIds = Array.isArray(record.hiddenColumnIds)
    ? record.hiddenColumnIds.filter((entry): entry is string => typeof entry === "string" && entry.trim() !== "")
    : [];
  return { columnOrder, hiddenColumnIds };
}

export function serializeHubDataTableColumnLayoutSnapshot(
  snapshot: HubDataTableColumnLayoutSnapshot,
): HubDataTableColumnLayoutSnapshot & { v: number } {
  return { v: SNAPSHOT_VERSION, ...snapshot };
}

/** Merge API column ids with a saved layout; drops stale ids and appends new columns at the end. */
export function mergeColumnIdsWithLayout(
  apiColumnIds: readonly string[],
  saved: HubDataTableColumnLayoutSnapshot | null,
): { orderedIds: string[]; hiddenIds: Set<string> } {
  const apiSet = new Set(apiColumnIds);
  const hiddenIds = new Set((saved?.hiddenColumnIds ?? []).filter((id) => apiSet.has(id)));
  const savedOrder = (saved?.columnOrder ?? []).filter((id) => apiSet.has(id));
  const orderedIds = [...savedOrder, ...apiColumnIds.filter((id) => !savedOrder.includes(id))];
  const visibleCount = orderedIds.filter((id) => !hiddenIds.has(id)).length;
  if (visibleCount === 0 && orderedIds.length > 0) {
    hiddenIds.clear();
  }
  return { orderedIds, hiddenIds };
}

export function reorderColumnIds(columnIds: readonly string[], draggedId: string, targetId: string): string[] {
  if (draggedId === targetId) {
    return [...columnIds];
  }
  const withoutDragged = columnIds.filter((id) => id !== draggedId);
  const targetIndex = withoutDragged.indexOf(targetId);
  if (targetIndex < 0) {
    return [...columnIds];
  }
  const next = [...withoutDragged];
  next.splice(targetIndex, 0, draggedId);
  return next;
}

export function toggleColumnHidden(
  columnIds: readonly string[],
  hiddenColumnIds: readonly string[],
  columnId: string,
  visible: boolean,
): string[] {
  const hidden = new Set(hiddenColumnIds);
  if (visible) {
    hidden.delete(columnId);
  } else {
    const visibleCount = columnIds.filter((id) => !hidden.has(id) && id !== columnId).length;
    if (visibleCount === 0) {
      return [...hiddenColumnIds];
    }
    hidden.add(columnId);
  }
  return [...hidden];
}
